/**
 * The states a change instance can be in, and the colour each one wears on the
 * ring — the colours of the tags those same states wear everywhere else, so a
 * slice and a tag are recognisably the same thing.
 *
 * They are the tags' own values: APPROVED and ERROR are the hexes
 * `getTagForStates` passes, REJECTED is antd's orange preset, and PENDING is
 * grey because its tag is the default one.
 *
 * These were picked to be read at tag size with their name beside them, which
 * is a different job from filling a slice, and it shows:
 *
 *  - ERROR's #ff7875 is 4.3 ΔE from PENDING's grey under protanopia and 7.7
 *    from REJECTED's orange under deuteranopia. It has two neighbours on a
 *    ring and only one safe partner, so one weak pair is unavoidable. The
 *    ORDER below puts the grey one at the wrap — that pair is 18.9 ΔE under
 *    normal vision, so only protanopes are affected, where orange↔salmon is
 *    10.3 and would be hard for everyone.
 *  - Three of the four sit under 3:1 against white (1.98 to 2.56), so a slice
 *    is faint. Each carries its state and count on hover and in the ring's
 *    label, but there is no visible text on the tile to fall back on.
 *
 * Darker steps of the same hues would clear both. That is the trade: exact
 * agreement with the tags, at the cost of a ring that is fainter and, for one
 * pair, harder for some readers.
 */
export const CHANGE_STATE_COLORS: Record<string, string> = {
    /** the default tag is grey, so the slice is too */
    PENDING: '#8c8c8c',
    APPROVED: '#69c0ff',
    /** antd's orange preset, which is what the tag asks for by name */
    REJECTED: '#fa8c16',
    ERROR: '#ff7875',
    COMPLETED: '#95de64',
    CLOSED: '#8c8c8c',
};

/**
 * The states that mean there is still something outstanding, in the order they
 * are drawn. COMPLETED and CLOSED are done with, so they are not on the ring —
 * an application with nothing here has nothing left to answer for.
 *
 * The order keeps ERROR away from REJECTED; see the note above.
 */
export const OUTSTANDING_STATES = ['PENDING', 'REJECTED', 'APPROVED', 'ERROR'] as const;

export type OutstandingState = (typeof OUTSTANDING_STATES)[number];

/** what each state is called on a legend, where there is no room for shouting */
export const STATE_LABELS: Record<string, string> = {
    PENDING: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    ERROR: 'Error',
};

/** the counts the API sends, keyed by the state they stand for */
export const outstandingCounts = (record: any): Record<string, number> => ({
    PENDING: record?.num_pending_changes ?? 0,
    APPROVED: record?.num_approved_changes ?? 0,
    REJECTED: record?.num_rejected_changes ?? 0,
    ERROR: record?.num_error_changes ?? 0,
});
